import React from 'react'
import { Toaster } from 'react-hot-toast'
import { cn } from '../../utils/cn'

interface LayoutProps {
  children: React.ReactNode
  className?: string
}

const Layout: React.FC<LayoutProps> = ({ children, className }) => {
  return (
    <div className="min-h-screen-mobile bg-background-light dark:bg-background-dark transition-colors duration-200">
      {/* Desktop Header */}
      <header className="hidden lg:block sticky top-0 z-30 bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-md border-b border-secondary-200 dark:border-secondary-700">
        <div className="container-mobile py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="lg:hidden">
                {/* Mobile menu button */}
              </button>
              <h1 className="text-xl font-bold text-secondary-900 dark:text-white">
                V2Board Manager
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="search"
                  placeholder="Tìm kiếm..."
                  className="input pl-10"
                />
              </div>

              {/* Notifications */}
              <button className="relative p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-600 dark:text-secondary-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="absolute top-1 right-1 w-2 h-2 bg-error-500 rounded-full animate-pulse" />
              </button>

              {/* Quick Actions */}
              <button className="btn btn-primary gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Sync Now
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-30 bg-surface-light/90 dark:bg-surface-dark/90 backdrop-blur-md border-b border-secondary-200 dark:border-secondary-700 safe-top">
        <div className="container-mobile py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-base text-secondary-900 dark:text-white">
                  V2Board Manager
                </h1>
                <p className="text-xs text-secondary-500 dark:text-secondary-400">
                  Dashboard
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Sync Status */}
              <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full bg-success-100 dark:bg-success-900/30 text-success-500 dark:text-success-400 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-success-500 animate-pulse" />
                All Systems OK
              </div>

              {/* Mobile Menu Toggle */}
              <button className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800">
                <svg className="w-5 h-5 text-secondary-600 dark:text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={cn('min-h-screen-mobile', className)}>
        {children}
      </main>

      {/* Status Bar (Mobile) */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-40 px-4">
        <div className="bg-surface-light dark:bg-surface-dark border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
              <span className="text-xs text-secondary-600 dark:text-secondary-400">
                Last sync: 2 mins ago
              </span>
            </div>
            <button className="text-xs font-medium text-primary-600 dark:text-primary-400">
              View Details →
            </button>
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <Toaster
        position="bottom-center"
        containerClassName="safe-bottom"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--surface-light)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border)',
            borderRadius: '0.75rem',
            maxWidth: 'calc(100vw - 2rem)',
          },
        }}
      />
    </div>
  )
}

export default Layout
